import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import GroupIcon from '@mui/icons-material/Group';
import { getUserPermissions, updateUser, updateUserPermissions } from '../api/usersApi';
import { PERMISSION_GROUPS, ROLES, ROLE_DEFAULT_PERMISSIONS, getRoleLabel } from '../model/userConstants';
import { usePermissions } from '../../../shared/auth/PermissionsContext';

const MODULE_ICONS = {
  Pacientes: PersonIcon,
  'Pruebas y valoraciones': AssignmentIcon,
  Datos: ImportExportIcon,
  Usuarios: GroupIcon,
};

/**
 * Diálogo de gestión de permisos de un usuario: selector de rol y
 * interruptores por funcionalidad. Los cambios respecto a los permisos
 * por defecto del rol se guardan como excepciones (concesión/revocación).
 */
export default function UserPermissionsDialog({ user, open, onClose, onSaved }) {
  const { hasPermission, refreshPermissions } = usePermissions();
  const canManageAdmins = hasPermission('users:manage_admins');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [role, setRole] = useState('SANITARIO');
  const [roleDefaults, setRoleDefaults] = useState([]);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    setError('');
    setLoading(true);
    getUserPermissions(user.id)
      .then((data) => {
        setRole(data.role);
        setRoleDefaults(data.roleDefaults || []);
        const next = {};
        PERMISSION_GROUPS.forEach((group) => {
          group.permissions.forEach(({ key }) => {
            next[key] = (data.effective || []).includes(key);
          });
        });
        setChecked(next);
      })
      .catch((err) => {
        console.error('Error al cargar los permisos del usuario:', err);
        setError('No se pudieron cargar los permisos del usuario.');
      })
      .finally(() => setLoading(false));
  }, [open, user]);

  const isSuperadmin = role === 'SUPERADMIN';

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    // Al cambiar de rol se parte de sus defaults, sin excepciones previas
    const defaults = ROLE_DEFAULT_PERMISSIONS[nextRole] || [];
    setRoleDefaults(defaults);
    const next = {};
    PERMISSION_GROUPS.forEach((group) => {
      group.permissions.forEach(({ key }) => {
        next[key] = defaults.includes(key);
      });
    });
    setChecked(next);
  };

  const overrides = useMemo(() => {
    if (isSuperadmin) return [];
    const result = [];
    PERMISSION_GROUPS.forEach((group) => {
      group.permissions.forEach(({ key }) => {
        const byDefault = roleDefaults.includes(key);
        const wanted = !!checked[key];
        if (wanted !== byDefault) {
          result.push({ permission: key, granted: wanted });
        }
      });
    });
    return result;
  }, [checked, roleDefaults, isSuperadmin]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (user.role !== role) {
        await updateUser({ userId: user.id, payload: { role } });
      }
      if (!isSuperadmin) {
        await updateUserPermissions({ userId: user.id, overrides });
      }
      await refreshPermissions();
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error al guardar los permisos:', err);
      setError(err?.response?.data?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Avatar sx={{ bgcolor: '#0D6AEE' }}><AdminPanelSettingsIcon /></Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Permisos de {user.nombre || user.username}</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>@{user.username}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f8fafc' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <FormControl fullWidth sx={{ mb: 0.5, backgroundColor: '#fff', borderRadius: 2 }}>
              <InputLabel>Rol</InputLabel>
              <Select
                value={role}
                label="Rol"
                disabled={!canManageAdmins}
                onChange={(e) => handleRoleChange(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {Object.keys(ROLES).map((key) => (
                  <MenuItem key={key} value={key}>{getRoleLabel(key)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!canManageAdmins ? (
              <Typography variant="caption" color="text.secondary">
                Solo un administrador total puede cambiar el rol.
              </Typography>
            ) : null}

            {isSuperadmin ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Un administrador total siempre tiene todos los permisos.
              </Alert>
            ) : (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {PERMISSION_GROUPS.map((group) => {
                  const ModuleIcon = MODULE_ICONS[group.module] || GroupIcon;
                  return (
                    <Paper key={group.module} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e3e8ef', backgroundColor: '#fff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ModuleIcon fontSize="small" sx={{ color: '#0D6AEE' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>{group.module}</Typography>
                      </Box>
                      {group.permissions.map(({ key, label }) => {
                        const byDefault = roleDefaults.includes(key);
                        const isException = !!checked[key] !== byDefault;
                        return (
                          <Box key={key} display="flex" alignItems="center" justifyContent="space-between">
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={!!checked[key]}
                                  onChange={(e) => setChecked((prev) => ({ ...prev, [key]: e.target.checked }))}
                                />
                              )}
                              label={label}
                            />
                            {isException ? (
                              <Chip size="small" color="warning" label={checked[key] ? 'Concedido' : 'Revocado'} />
                            ) : null}
                          </Box>
                        );
                      })}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ borderRadius: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving} sx={{ borderRadius: 2, px: 3 }}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
